// src/app/guards/auth.guard.ts
import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { UserStore } from '@app/core/store/user.store';
import { Observable, interval } from 'rxjs';
import { map, take, takeWhile } from 'rxjs/operators';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(private readonly userStore: UserStore, private readonly router: Router) {}

  canActivate(
    _next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    if (!environment.production) {
      console.log('🛡️ AuthGuard: Vérification d\'accès pour route:', state.url);
      console.log('🛡️ AuthGuard: Timestamp:', new Date().toISOString());
    }
    
    // Vérifier d'abord si on a un JWT valide en localStorage
    const jwtToken = localStorage.getItem('jwtToken');
    if (!environment.production) {
      console.log('🔍 AuthGuard: JWT en localStorage:', jwtToken ? 'EXISTE' : 'NULL');
      console.log('🔍 AuthGuard: Valeur JWT:', jwtToken?.substring(0, 50) + '...');
    }
    
    if (jwtToken) {
      if (!environment.production) {
        console.log('✅ AuthGuard: JWT trouvé, autorisation accordée');
      }
      return true;
    } else {
      if (!environment.production) {
        console.log('❌ AuthGuard: Pas de JWT en localStorage');
      }
    }
    
    // Sinon, vérifier si l'utilisateur est déjà connecté et l'initialisation terminée
    const user = this.userStore.user();
    const isInitialized = this.userStore.isInitialized();
    
    if (isInitialized) {
      if (user) {
        if (!environment.production) {
          console.log('✅ AuthGuard: Utilisateur connecté dans le store');
        }
        return true;
      } else {
        if (!environment.production) {
          console.log('❌ AuthGuard: Pas d\'utilisateur, redirection vers login');
        }
        this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
        return false;
      }
    }

    // Attendre que l'initialisation soit terminée
    if (!environment.production) {
      console.log('⏳ AuthGuard: Attente de la fin d\'initialisation...');
    }
    return interval(100).pipe(
      takeWhile(() => !this.userStore.isInitialized(), true),
      map(() => {
        const finalUser = this.userStore.user();
        const finalInitialized = this.userStore.isInitialized();
        
        if (finalInitialized) {
          if (finalUser) {
            if (!environment.production) {
              console.log('✅ AuthGuard: Utilisateur connecté après initialisation');
            }
            return true;
          } else {
            if (!environment.production) {
              console.log('❌ AuthGuard: Pas d\'utilisateur après initialisation, redirection vers login');
            }
            this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
            return false;
          }
        }
        return false; // Continue waiting
      }),
      take(1)
    );
  }
}
